import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import Select from 'react-select'
import { CompetitorSelect, SeasonSelect } from '../components'
import type { Competitor, Season } from '../types/forms'
import CreatableSelect from 'react-select/creatable'
import { Banner, Button, ToggleGroup } from '../components'
import { createVodAsset } from '../services/vodUpload'
import { fetchVodTypes } from '../services/vodApi'
import { api } from '../services/api'
import { useToast } from '../contexts/ToastContext'
import { convertPTInputToUTC } from '../utils/timezone'
import { UUID_PATTERN } from '../utils/uuid'
import pageStyles from '../styles/page.module.scss'
import formStyles from '../styles/forms.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import styles from './VodCreate.module.scss'

type GatingChoice = 'inherit' | 'gated' | 'free'

/**
 * Metadata-first creation of a first-party VOD asset (created HIDDEN, no
 * media). The master video is uploaded afterwards from the asset detail page.
 */
const VodCreate: React.FC = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [typeCode, setTypeCode] = useState<string>('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [synopsis, setSynopsis] = useState('')
  const [matchId, setMatchId] = useState('')
  const [competitors, setCompetitors] = useState<Competitor[]>([])
  const [seasons, setSeasons] = useState<Season[]>([])
  const [matchIdError, setMatchIdError] = useState<string | null>(null)
  const [displayDate, setDisplayDate] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [gating, setGating] = useState<GatingChoice>('inherit')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const {
    data: typesData,
    error: typesError,
    mutate: retryTypes,
  } = useSWR('vod-types', fetchVodTypes, {
    revalidateOnFocus: false,
  })
  // The merchandising vocabulary the VOD detail page edits, not the
  // VOD-specific tag table ingestion fills from player metadata.
  const { data: tagsData } = useSWR<{
    tags: Array<{ value: string; label: string }>
  }>('publishing-tag-options', () => api.get('/publishing/tag-options'), {
    revalidateOnFocus: false,
  })

  const typeOptions = useMemo(
    () =>
      (typesData?.types ?? []).map((type) => ({
        value: type.vod_type_code,
        label: type.display_name,
      })),
    [typesData]
  )
  const tagOptions = tagsData?.tags ?? []

  const selectedType = typesData?.types.find((t) => t.vod_type_code === typeCode)
  const typeDefaultLabel = selectedType
    ? selectedType.default_requires_subscription
      ? 'Gated (subscription required)'
      : 'Free'
    : 'Gated (subscription required)'

  const gatingOptions = [
    {
      value: 'inherit' as const,
      label: 'Type default',
      description: `Inherits the type's gating default — currently: ${typeDefaultLabel}.`,
    },
    {
      value: 'gated' as const,
      label: 'Gated',
      description: 'Explicitly requires a subscription, regardless of the type default.',
    },
    {
      value: 'free' as const,
      label: 'Free',
      description: 'Deliberately opens this asset up to everyone — an explicit choice.',
    },
  ]

  const isValid = typeCode !== '' && title.trim().length > 0

  const handleCreate = async () => {
    if (!isValid || isSaving) return
    setSaveError(null)
    const trimmedMatchId = matchId.trim()
    // Same pair the detail page takes: our UUID, or the Sportradar id an
    // editor is more likely to be holding (prefixed or bare digits).
    const matchIsUuid = UUID_PATTERN.test(trimmedMatchId)
    const matchIsSrId = /^(sr:sport_event:)?\d+$/.test(trimmedMatchId)
    if (trimmedMatchId && !matchIsUuid && !matchIsSrId) {
      setMatchIdError('Enter a match UUID or a Sportradar id.')
      return
    }
    setIsSaving(true)
    try {
      const { asset } = await createVodAsset({
        vod_type_code: typeCode,
        tc_title: title.trim(),
        tc_description: description.trim() || undefined,
        tc_synopsis: synopsis.trim() || undefined,
        display_date: displayDate ? convertPTInputToUTC(displayDate) : undefined,
        // Either identifier, the way the detail page takes it; the server
        // resolves the Sportradar one and rejects both together.
        match_id: matchIsUuid ? trimmedMatchId : undefined,
        ext_event_id: !matchIsUuid && trimmedMatchId ? trimmedMatchId : undefined,
        player_ids:
          competitors.length > 0 ? competitors.map((c) => c.tc_id) : undefined,
        season_ids: seasons.length > 0 ? seasons.map((s) => s.tc_id) : undefined,
        tags: tags.length > 0 ? tags : undefined,
        requires_subscription:
          gating === 'inherit' ? null : gating === 'gated',
      })
      toast('VOD created', 'success')
      navigate(`/vods/${asset.tc_vod_asset_id}`)
    } catch (err) {
      setSaveError((err as Error).message || 'Failed to create the VOD asset')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.pageHeader}>
        <h1>New VOD</h1>
        <p>
          The asset is created hidden, without media — upload the master video from its
          detail page afterwards.
        </p>
      </div>

      {saveError && (
        <Banner
          variant="error"
          message={saveError}
          onDismiss={() => setSaveError(null)}
          autoScroll
        />
      )}

      <div className={formStyles.formContainer}>
        <div className={styles.form}>
          <div className={styles.fieldGroup}>
            <label htmlFor="vodCreateType">
              Type <span className={styles.required}>*</span>
            </label>
            {typesError ? (
              <Banner
                variant="error"
                message={`Failed to load VOD types: ${(typesError as Error).message}`}
                action={
                  <Button variant="secondary" size="small" onClick={() => retryTypes()}>
                    Retry
                  </Button>
                }
              />
            ) : (
              <Select
                inputId="vodCreateType"
                value={typeOptions.find((opt) => opt.value === typeCode) || null}
                onChange={(option) => setTypeCode(option?.value || '')}
                options={typeOptions}
                isLoading={!typesData}
                placeholder="Select a VOD type…"
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
              />
            )}
            {selectedType?.description && (
              <p className={styles.fieldHint}>{selectedType.description}</p>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="vodCreateTitle">
              Title <span className={styles.required}>*</span>
            </label>
            <input
              id="vodCreateTitle"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={styles.input}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="vodCreateDescription">Description</label>
            <textarea
              id="vodCreateDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={styles.textarea}
              rows={3}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="vodCreateSynopsis">Synopsis</label>
            <textarea
              id="vodCreateSynopsis"
              value={synopsis}
              onChange={(e) => setSynopsis(e.target.value)}
              className={styles.textarea}
              rows={3}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="vodCreateMatchId">Match</label>
            <input
              id="vodCreateMatchId"
              type="text"
              value={matchId}
              onChange={(e) => {
                setMatchId(e.target.value)
                setMatchIdError(null)
              }}
              placeholder="Match UUID or Sportradar id"
              className={styles.input}
            />
            {matchIdError && <p className={styles.errorText}>{matchIdError}</p>}
            <p className={styles.fieldHint}>
              Optional. Takes our match UUID or a Sportradar id
              (sr:sport_event:47608611, or just the digits). A linked match sets
              the display date to its start time, and adds its competitors and
              season.
            </p>
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="vodCreatePlayers">Players &amp; Teams</label>
            <CompetitorSelect
              isMulti
              teamType="mixed"
              value={competitors.length > 0 ? competitors : null}
              onChange={(value) =>
                setCompetitors(
                  Array.isArray(value) ? value : value ? [value] : []
                )
              }
              placeholder="Search players and teams…"
            />
            <p className={styles.fieldHint}>
              Optional. A linked match adds its own competitors on top of these.
            </p>
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="vodCreateSeasons">Tournaments</label>
            <SeasonSelect
              isMulti
              value={seasons.length > 0 ? seasons : null}
              onChange={(value) =>
                setSeasons(Array.isArray(value) ? value : value ? [value] : [])
              }
              placeholder="Search tournaments…"
            />
            <p className={styles.fieldHint}>
              Optional. A linked match adds its own season on top of these.
            </p>
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="vodCreateDisplayDate">Display Date</label>
            <input
              id="vodCreateDisplayDate"
              type="datetime-local"
              value={displayDate}
              onChange={(e) => setDisplayDate(e.target.value)}
              className={styles.input}
            />
            <p className={styles.fieldHint}>
              Orders the asset in rails. Leave empty to default to now (or the linked
              match&apos;s day, once linked).
            </p>
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="vodCreateTags">Merchandising Tags</label>
            <CreatableSelect
              inputId="vodCreateTags"
              isMulti
              options={tagOptions}
              value={tags.map((tag) => {
                const option = tagOptions.find((opt) => opt.value === tag)
                return { value: tag, label: option?.label || tag }
              })}
              onChange={(selected) => setTags(selected ? selected.map((opt) => opt.value) : [])}
              className={selectStyles.reactSelect}
              classNamePrefix="react-select"
              placeholder="Type to create or select tags…"
              formatCreateLabel={(inputValue) => `Create tag "${inputValue}"`}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label>Access</label>
            <ToggleGroup options={gatingOptions} value={gating} onChange={setGating} size="small" />
          </div>

          <div className={formStyles.formFooter}>
            <Button variant="secondary" onClick={() => navigate('/vods')} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!isValid || isSaving}>
              {isSaving ? 'Creating…' : 'Create Asset'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VodCreate
